import { useState, useEffect, useId, useRef, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { vencimentosService, type Vencimento, type ColaboradorSemData, type ResumoDashboardVencimentos } from '../services/vencimentos.service';
import { useEmpresa } from '../contexts/EmpresaContext';
import {
  CalendarDaysIcon,
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  CheckCircleIcon,
  ClockIcon,
  UserGroupIcon,
  InformationCircleIcon,
} from '@heroicons/react/24/outline';
import { formatCPF } from '../utils/formatters';
import { getHojeISO } from '../utils/dateUtils';
import { validateExcelFile, sanitizeSpreadsheetRows } from '../utils/fileSecurity';
import PageContainer from '../components/PageContainer';
import PageHeader from '../components/PageHeader';

export default function VencimentosExames() {
  const { empresa } = useEmpresa();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab =
    searchParams.get('tab') === 'sem_data'
      ? 'sem_data'
      : searchParams.get('tab') === 'vencidos'
      ? 'vencidos'
      : 'calendario';
  const [activeTab, setActiveTab] = useState<'calendario' | 'sem_data' | 'vencidos'>(initialTab);

  useEffect(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'sem_data' || tabParam === 'calendario' || tabParam === 'vencidos') {
      setActiveTab(tabParam);
    }
  }, [searchParams]);
  const [vencimentos, setVencimentos] = useState<Record<string, Vencimento[]>>({});
  const [colaboradoresSemData, setColaboradoresSemData] = useState<ColaboradorSemData[]>([]);
  const [resumo, setResumo] = useState<ResumoDashboardVencimentos | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [mesAtivo, setMesAtivo] = useState('');

  // Inline update states for "sem_data"
  const [inlineDates, setInlineDates] = useState<Record<number, string>>({});
  const [savingInline, setSavingInline] = useState<Record<number, boolean>>({});
  const [actionFeedback, setActionFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // File input ref for Excel upload
  const fileInputRef = useRef<HTMLInputElement>(null);
  const fileInputId = useId();
  const [isUploadingExcel, setIsUploadingExcel] = useState(false);

  const [loadingSemData, setLoadingSemData] = useState(false);
  const semDataLoadedRef = useRef(false);

  const loadSemData = async () => {
    if (semDataLoadedRef.current) return;
    try {
      setLoadingSemData(true);
      const semData = await vencimentosService.getColaboradoresSemData();
      setColaboradoresSemData(semData);
      semDataLoadedRef.current = true;
    } catch (err) {
      console.error('❌ Erro ao carregar colaboradores sem data:', err);
    } finally {
      setLoadingSemData(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'sem_data' && !semDataLoadedRef.current && empresa?.id) {
      loadSemData();
    }
  }, [activeTab, empresa?.id]);

  useEffect(() => {
    if (empresa?.id) {
      loadAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresa?.id]);

  const loadAllData = async () => {
    setIsLoading(true);
    try {
      const [vencimentosData, resumoData] = await Promise.all([
        vencimentosService.getVencimentos(),
        vencimentosService.getResumoDashboard(),
      ]);

      setVencimentos(vencimentosData);
      setResumo(resumoData);

      if (activeTab === 'sem_data') {
        loadSemData();
      }

      // Definir mês atual como ativo
      const hoje = new Date();
      const mesAtual = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`;
      setMesAtivo(mesAtual);
    } catch (error) {
      console.error('❌ Erro ao carregar dados de vencimentos:', error);
      setActionFeedback({ type: 'error', message: 'Erro ao carregar os dados de vencimentos.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Gerar lista de meses (mês atual + 12 meses)
  const hoje = useMemo(() => new Date(), []);
  const hojeFormatado = useMemo(() => hoje.toLocaleDateString('pt-BR'), [hoje]);
  const limite90Formatado = useMemo(() => {
    const d = new Date(hoje);
    d.setDate(d.getDate() + 90);
    return d.toLocaleDateString('pt-BR');
  }, [hoje]);
  const mesAtualChave = useMemo(
    () => `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}`,
    [hoje],
  );

  const meses: Array<{
    chave: string;
    label: string;
    isMesAtual: boolean;
    isProximos3Meses: boolean;
  }> = [];
  for (let i = 0; i < 13; i++) {
    const data = new Date(hoje.getFullYear(), hoje.getMonth() + i, 1);
    const chave = `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}`;
    const label = data.toLocaleDateString('pt-BR', { year: 'numeric', month: 'long' });
    meses.push({
      chave,
      label,
      isMesAtual: i === 0,
      isProximos3Meses: i >= 1 && i <= 3,
    });
  }

  const vencimentosMesAtivo = mesAtivo ? vencimentos[mesAtivo] || [] : [];

  const contagemMesAtual = useMemo(() => {
    const hojeData = new Date(hoje);
    hojeData.setHours(0, 0, 0, 0);
    const lista = mesAtivo ? vencimentos[mesAtivo] || [] : [];
    const vencidosNoMes = lista.filter((v) => new Date(v.data_vencimento) < hojeData).length;
    const aVencerNoMes = lista.length - vencidosNoMes;
    return { total: lista.length, vencidos: vencidosNoMes, aVencer: aVencerNoMes };
  }, [mesAtivo, vencimentos, hoje]);

  const parseExcelDate = (val: unknown): string | null => {
    if (!val) return null;

    if (typeof val === 'string') {
      const trimmed = val.trim();
      if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
        return trimmed;
      }
      const matchBR = trimmed.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (matchBR) {
        const day = matchBR[1].padStart(2, '0');
        const month = matchBR[2].padStart(2, '0');
        const year = matchBR[3];
        return `${year}-${month}-${day}`;
      }
    }

    if (typeof val === 'number') {
      // Data serializada do Excel
      const parsedDate = new Date(Math.round((val - 25569) * 86400 * 1000));
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toISOString().split('T')[0];
      }
    }

    if (val instanceof Date && !isNaN(val.getTime())) {
      return val.toISOString().split('T')[0];
    }

    return null;
  };

  // Handler para salvar data inline de um colaborador
  const handleSaveInlineDate = async (colaboradorId: number) => {
    const dataUltimo = inlineDates[colaboradorId];
    if (!dataUltimo) {
      setActionFeedback({ type: 'error', message: 'Selecione uma data válida antes de salvar.' });
      return;
    }

    const hojeStr = getHojeISO();
    if (dataUltimo > hojeStr) {
      setActionFeedback({
        type: 'error',
        message: 'A data do último exame realizado não pode ser futura.',
      });
      return;
    }

    setSavingInline((prev) => ({ ...prev, [colaboradorId]: true }));
    try {
      await vencimentosService.atualizarLoteDatas([
        { colaborador_id: colaboradorId, data_ultimo_exame: dataUltimo },
      ]);
      setActionFeedback({ type: 'success', message: 'Data do último exame atualizada com sucesso!' });
      // Recarregar os dados
      await loadAllData();
    } catch (error: unknown) {
      console.error('❌ Erro ao salvar data individual:', error);
      const msg =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Erro ao salvar a data do exame.';
      setActionFeedback({ type: 'error', message: msg });
    } finally {
      setSavingInline((prev) => ({ ...prev, [colaboradorId]: false }));
    }
  };

  // Download do modelo Excel (.xlsx)
  const handleDownloadExcelTemplate = () => {
    const wsData = [
      ['ID', 'Nome', 'CPF', 'Cargo', 'Setor', 'Data_Ultimo_Exame_AAAA_MM_DD'],
      ...colaboradoresSemData.map((c) => [
        c.id,
        c.nome,
        c.cpf,
        c.cargo || '',
        c.setor || '',
        '', // A preencher pelo usuário
      ]),
    ];

    const ws = XLSX.utils.aoa_to_sheet(wsData);
    ws['!cols'] = [
      { wch: 10 },
      { wch: 35 },
      { wch: 18 },
      { wch: 25 },
      { wch: 22 },
      { wch: 32 },
    ];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Vencimentos');
    XLSX.writeFile(
      wb,
      `modelo_vencimentos_exames_${getHojeISO()}.xlsx`,
    );
  };

  // Upload e processamento do Excel (.xlsx / .xls)
  const handleUploadExcel = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const securityCheck = await validateExcelFile(file);
    if (!securityCheck.valid) {
      setActionFeedback({
        type: 'error',
        message: securityCheck.error || 'Por favor, selecione um arquivo Excel válido.',
      });
      return;
    }

    setIsUploadingExcel(true);
    setActionFeedback(null);

    try {
      const buffer = await file.arrayBuffer();
      const wb = XLSX.read(buffer, { type: 'array' });
      const sheetName = wb.SheetNames[0];
      const sheet = wb.Sheets[sheetName];
      const rawRows: Record<string, unknown>[] = XLSX.utils.sheet_to_json(sheet, { defval: '' });

      if (rawRows.length === 0) {
        throw new Error('A planilha está vazia ou não contém dados preenchidos.');
      }

      if (rawRows.length > 1000) {
        throw new Error('A planilha ultrapassa o limite de 1000 colaboradores por lote.');
      }

      // Higienização contra Formula Injection / CSV Injection / scripts
      const sanitizedRows = sanitizeSpreadsheetRows(rawRows);

      const hojeStr = getHojeISO();
      const updates: Array<{ colaborador_id: number; data_ultimo_exame: string }> = [];

      for (let i = 0; i < sanitizedRows.length; i++) {
        const row = sanitizedRows[i];
        // Encontrar ID
        const idVal = row['ID'] || row['id'] || row['Id'] || Object.values(row)[0];
        const id = parseInt(String(idVal).replace(/\D/g, ''), 10);

        // Encontrar Data (coluna com 'data' ou a 6a coluna)
        let rawDate = row['Data_Ultimo_Exame_AAAA_MM_DD'] || row['Data'] || row['data'];
        if (!rawDate && Object.values(row).length >= 6) {
          rawDate = Object.values(row)[5];
        }

        if (id && !isNaN(id) && rawDate) {
          const parsedDate = parseExcelDate(rawDate);
          if (!parsedDate) {
            throw new Error(
              `Linha ${i + 2}: Formato de data inválido "${rawDate}". Use o padrão AAAA-MM-DD ou DD/MM/AAAA.`,
            );
          }

          if (parsedDate > hojeStr) {
            throw new Error(
              `Linha ${i + 2}: A data informada (${parsedDate}) é futura. Exames realizados devem ter data passada ou atual.`,
            );
          }

          updates.push({
            colaborador_id: id,
            data_ultimo_exame: parsedDate,
          });
        }
      }

      if (updates.length === 0) {
        throw new Error(
          'Nenhuma data de exame foi preenchida na planilha. Preencha a coluna "Data_Ultimo_Exame_AAAA_MM_DD".',
        );
      }

      const res = await vencimentosService.atualizarLoteDatas(updates);
      setActionFeedback({
        type: 'success',
        message: `${res.atualizados || updates.length} colaborador(es) atualizado(s) com sucesso via planilha Excel!`,
      });

      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      await loadAllData();
    } catch (err: unknown) {
      console.error('❌ Erro no processamento do Excel:', err);
      const errorMsg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        (err instanceof Error ? err.message : 'Falha ao processar o arquivo Excel.');
      setActionFeedback({
        type: 'error',
        message: errorMsg,
      });
    } finally {
      setIsUploadingExcel(false);
    }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Vencimentos de Exames"
        description="Controle de validade legal dos exames periódicos (NR-7) e gestão de renovações"
      />

        {/* Feedback Alert */}
        {actionFeedback && (
          <div
            className={`mb-6 p-4 rounded-lg flex items-center justify-between ${
              actionFeedback.type === 'success'
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-800'
                : 'bg-red-50 border border-red-200 text-red-800'
            }`}
          >
            <div className="flex items-center gap-3">
              {actionFeedback.type === 'success' ? (
                <CheckCircleIcon className="h-5 w-5 text-emerald-600 flex-shrink-0" />
              ) : (
                <ExclamationTriangleIcon className="h-5 w-5 text-red-600 flex-shrink-0" />
              )}
              <p className="text-sm font-medium">{actionFeedback.message}</p>
            </div>
            <button
              onClick={() => setActionFeedback(null)}
              className="text-xs font-semibold underline hover:opacity-75"
            >
              Fechar
            </button>
          </div>
        )}

        {/* Banner Explicativo NR-7 vs SLA */}
        <div className="mb-6 bg-blue-50 border-l-4 border-blue-600 p-4 rounded-r-lg shadow-sm">
          <div className="flex items-start gap-3">
            <InformationCircleIcon className="h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-900">
              <p className="font-semibold mb-1">
                Critério de Validade Legal (NR-7 / PCMSO):
              </p>
              <p className="text-blue-800">
                A validade ocupacional é legal ({resumo?.vigencia_clinico_label ? `exame clínico ${resumo.vigencia_clinico_label}` : 'exame clínico padrão 12 meses'} ou conforme periodicidade própria dos complementares no PCMSO).
                Quando o colaborador possui exames complementares na sua função, a data de renovação é regida pelo{' '}
                <strong>exame que vence primeiro</strong>, garantindo total conformidade legal para a empresa.
              </p>
            </div>
          </div>
        </div>

        {/* Cards de Métricas e Destaque */}
        <div
          className={`grid grid-cols-1 ${
            (resumo?.count_vencidos ?? 0) > 0 ? 'md:grid-cols-4' : 'md:grid-cols-3'
          } gap-6 mb-8`}
        >
          {/* Card Exames Já Vencidos (se houver) */}
          {(resumo?.count_vencidos ?? 0) > 0 && (
            <div
              onClick={() => {
                setActiveTab('vencidos');
                setSearchParams({ tab: 'vencidos' });
              }}
              className="bg-white rounded-xl shadow-sm border border-rose-300 p-6 relative overflow-hidden cursor-pointer hover:border-rose-400 hover:shadow-md transition"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-rose-700">Exames Já Vencidos</p>
                  <p className="text-3xl font-extrabold text-rose-900 mt-2">
                    {resumo?.count_vencidos ?? 0}
                  </p>
                  <p className="text-xs text-rose-600 mt-1 font-medium">Expirados no passado • Clique para ver</p>
                </div>
                <div className="p-3 bg-rose-100 rounded-xl">
                  <ExclamationTriangleIcon className="h-8 w-8 text-rose-600" />
                </div>
              </div>
            </div>
          )}

          {/* Card Próximos 3 Meses */}
          <div className="bg-white rounded-xl shadow-sm border border-amber-200 p-6 relative overflow-hidden">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold text-amber-800">A Vencer nos Próximos 90 Dias</p>
                <p className="text-3xl font-extrabold text-amber-950 mt-2">
                  {resumo?.count_proximos_3_meses ?? 0}
                </p>
                <p className="text-xs text-amber-700 mt-1 font-medium">
                  Janela de 90 dias a partir de hoje ({hojeFormatado} a {limite90Formatado})
                </p>
              </div>
              <div className="p-3 bg-amber-100 rounded-xl">
                <ClockIcon className="h-8 w-8 text-amber-600" />
              </div>
            </div>
          </div>

          {/* Card Sem Data */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Colaboradores Sem Histórico</p>
                <p className="text-3xl font-extrabold text-gray-900 mt-2">
                  {resumo?.count_sem_data ?? colaboradoresSemData.length}
                </p>
                <p className="text-xs text-gray-500 mt-1">Necessitam informar data do último exame</p>
              </div>
              <div className="p-3 bg-gray-100 rounded-xl">
                <UserGroupIcon className="h-8 w-8 text-gray-600" />
              </div>
            </div>
          </div>

          {/* Card Periodicidade Padrão */}
          <div className="bg-white rounded-xl shadow-sm border border-emerald-200 p-6">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-1.5">
                  <p className="text-sm font-semibold text-emerald-800">Vigência do Exame Clínico (ASO)</p>
                  <span className="text-[10px] bg-emerald-100 text-emerald-800 font-bold px-1.5 py-0.5 rounded">
                    NR-07
                  </span>
                </div>
                <p className="text-3xl font-extrabold text-emerald-950 mt-2">
                  {resumo?.vigencia_clinico_label || resumo?.periodicidade_padrao_label || '12 Meses'}
                </p>
                <p className="text-xs text-emerald-700 mt-1">
                  {resumo?.vigencia_clinico_descricao || resumo?.periodicidade_padrao_descricao || 'Renovação anual conforme NR-7'}
                </p>

                {Boolean(resumo?.menor_prazo_complementares_meses) && (
                  <div className="mt-2.5 pt-2 border-t border-emerald-100 text-[11px] text-slate-600 flex items-center gap-1 flex-wrap">
                    <span className="font-semibold text-emerald-900">Complementares:</span>
                    <span>
                      {resumo?.menor_prazo_complementares_meses === resumo?.maior_prazo_complementares_meses
                        ? `${resumo?.menor_prazo_complementares_meses} meses`
                        : `de ${resumo?.menor_prazo_complementares_meses} a ${resumo?.maior_prazo_complementares_meses} meses`}
                    </span>
                    {Boolean(resumo?.total_cargos_com_complementares) && (
                      <span className="text-[10px] text-gray-400">
                        ({resumo?.total_cargos_com_complementares} cargo(s) com complementares)
                      </span>
                    )}
                  </div>
                )}
              </div>
              <div className="p-3 bg-emerald-100 rounded-xl shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-emerald-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Navegação entre Abas Principais */}
        <div className="flex border-b border-gray-200 mb-6 bg-white rounded-t-lg px-4 pt-2 overflow-x-auto">
          {Boolean(
            (resumo?.count_vencidos ?? 0) > 0 ||
              (vencimentos['vencidos'] && vencimentos['vencidos'].length > 0),
          ) && (
            <button
              onClick={() => {
                setActiveTab('vencidos');
                setSearchParams({ tab: 'vencidos' });
              }}
              className={`flex items-center gap-2 py-4 px-6 border-b-2 font-semibold text-sm transition-colors whitespace-nowrap ${
                activeTab === 'vencidos'
                  ? 'border-rose-600 text-rose-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <ExclamationTriangleIcon className="h-5 w-5 text-rose-500" />
              <span>Exames Já Vencidos</span>
              <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded-full bg-rose-100 text-rose-800">
                {resumo?.count_vencidos ?? vencimentos['vencidos']?.length ?? 0}
              </span>
            </button>
          )}

          <button
            onClick={() => {
              setActiveTab('calendario');
              setSearchParams({});
            }}
            className={`flex items-center gap-2 py-4 px-6 border-b-2 font-semibold text-sm transition-colors whitespace-nowrap ${
              activeTab === 'calendario'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <CalendarDaysIcon className="h-5 w-5" />
            <span>Calendário por Mês (Previsão NR-7)</span>
          </button>

          <button
            onClick={() => {
              setActiveTab('sem_data');
              setSearchParams({ tab: 'sem_data' });
            }}
            className={`flex items-center gap-2 py-4 px-6 border-b-2 font-semibold text-sm transition-colors whitespace-nowrap ${
              activeTab === 'sem_data'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <UserGroupIcon className="h-5 w-5" />
            <span>Colaboradores Sem Histórico / Sem Data</span>
            {colaboradoresSemData.length > 0 && (
              <span className="ml-2 px-2 py-0.5 text-xs font-bold rounded-full bg-amber-100 text-amber-800">
                {colaboradoresSemData.length}
              </span>
            )}
          </button>
        </div>

        {isLoading ? (
          <div className="text-center py-16 bg-white rounded-b-lg shadow-sm">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto"></div>
            <p className="mt-4 text-gray-600 font-medium">Carregando vencimentos e colaboradores...</p>
          </div>
        ) : activeTab === 'vencidos' ? (
          /* Aba: Exames Já Vencidos */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-rose-50/40 flex items-center justify-between">
              <div>
                <h3 className="text-base font-bold text-rose-950 flex items-center gap-2">
                  <ExclamationTriangleIcon className="h-5 w-5 text-rose-600" />
                  Exames Ocupacionais com Validade Expirada
                </h3>
                <p className="text-xs text-rose-700 mt-0.5">
                  Estes exames ultrapassaram a periodicidade legal prevista pela NR-7 e exigem agendamento prioritário.
                </p>
              </div>
            </div>
            {(vencimentos['vencidos'] || []).length === 0 ? (
              <div className="text-center py-16">
                <CheckCircleIcon className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-gray-800 font-semibold">Nenhum exame ocupacional vencido pendente!</p>
                <p className="text-sm text-gray-400 mt-1">Sua empresa está em conformidade com o cronograma.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs text-gray-600">
                  <thead className="bg-gray-50/90 text-[11px] uppercase font-bold text-gray-500 border-b border-gray-200">
                    <tr>
                      <th className="py-2.5 px-3 min-w-[140px] max-w-[180px]">
                        Colaborador
                      </th>
                      <th className="py-2.5 px-3 whitespace-nowrap">
                        CPF
                      </th>
                      <th className="py-2.5 px-3 min-w-[150px] max-w-[200px]">
                        Cargo / Setor
                      </th>
                      <th className="py-2.5 px-3 whitespace-nowrap">
                        Exame Disparador (NR-7)
                      </th>
                      <th className="py-2.5 px-3 whitespace-nowrap">
                        Último Exame
                      </th>
                      <th className="py-2.5 px-3 whitespace-nowrap">
                        Validade Legal
                      </th>
                      <th className="py-2.5 px-3 whitespace-nowrap">
                        Vencimento
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {(vencimentos['vencidos'] || []).map((v, index) => {
                      const vencData = new Date(v.data_vencimento);
                      return (
                        <tr key={index} className="hover:bg-rose-50/20 transition-colors">
                          <td className="py-2.5 px-3">
                            <div className="font-semibold text-gray-900 leading-snug">{v.colaborador_nome}</div>
                            {v.total_exames_colaborador && v.total_exames_colaborador > 1 && (
                              <span className="text-[10px] text-gray-500">
                                Bateria com {v.total_exames_colaborador} exames
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 whitespace-nowrap text-gray-600 font-mono">
                            {formatCPF(v.colaborador_cpf)}
                          </td>
                          <td className="py-2.5 px-3">
                            <div className="text-gray-900 font-medium leading-snug">{v.colaborador_cargo || '-'}</div>
                            <div className="text-[11px] text-gray-500">{v.colaborador_setor || '-'}</div>
                            {v.exposto_a_risco !== undefined && (
                              <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold mt-1 ${
                                  v.exposto_a_risco
                                    ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                    : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                }`}
                                title={v.motivo_periodicidade}
                              >
                                {v.exposto_a_risco ? '⚠️ Exposto a Risco (PGR)' : '🛡️ Sem Risco Ocupacional'}
                              </span>
                            )}
                          </td>
                          <td className="py-2.5 px-3 whitespace-nowrap">
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-blue-50 text-blue-800 border border-blue-200">
                              {v.exame_disparador_nome || 'Exame Periódico'}
                            </span>
                          </td>
                          <td className="py-2.5 px-3 whitespace-nowrap text-gray-600">
                            {new Date(v.data_ultimo_exame).toLocaleDateString('pt-BR')}
                          </td>
                          <td className="py-2.5 px-3 whitespace-nowrap text-gray-700">
                            <div className="font-medium">{v.prazo_meses} meses</div>
                            {v.motivo_periodicidade && (
                              <div className="text-[10px] text-gray-400 max-w-[130px] truncate" title={v.motivo_periodicidade}>
                                {v.motivo_periodicidade}
                              </div>
                            )}
                          </td>
                          <td className="py-2.5 px-3 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-rose-100 text-rose-800 border border-rose-200">
                              {vencData.toLocaleDateString('pt-BR')} (Vencido)
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : activeTab === 'calendario' ? (
          <>
            {/* Tabs de Meses */}
            <div className="mb-6 bg-white rounded-lg shadow-sm overflow-hidden border border-gray-100">
              <div className="border-b border-gray-200">
                <nav className="flex overflow-x-auto py-1" aria-label="Tabs">
                  {meses.map((mes) => {
                    const count = vencimentos[mes.chave]?.length || 0;
                    const isActive = mesAtivo === mes.chave;
                    return (
                      <button
                        key={mes.chave}
                        onClick={() => setMesAtivo(mes.chave)}
                        className={`
                          flex-shrink-0 px-5 py-4 text-sm font-medium border-b-2 transition-all text-left
                          ${
                            isActive
                              ? 'border-primary-600 text-primary-600 bg-primary-50/40'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }
                        `}
                      >
                        <div className="flex flex-col gap-1">
                          <div className="flex items-center gap-2">
                            <span className="capitalize">{mes.label}</span>
                            {count > 0 && (
                              <span
                                className={`px-2 py-0.5 text-xs font-bold rounded-full ${
                                  isActive
                                    ? 'bg-primary-600 text-white'
                                    : 'bg-gray-200 text-gray-700'
                                }`}
                              >
                                {count}
                              </span>
                            )}
                          </div>
                          {mes.isMesAtual && (
                            <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-800 bg-emerald-100 px-1.5 py-0.5 rounded w-fit">
                              Mês Atual
                            </span>
                          )}
                          {mes.isProximos3Meses && (
                            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-800 bg-amber-100 px-1.5 py-0.5 rounded w-fit">
                              Próximos 3 meses
                            </span>
                          )}
                        </div>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </div>

            {/* Aviso esclarecedor para o mês atual quando houver vencidos nele */}
            {mesAtivo === mesAtualChave && contagemMesAtual.vencidos > 0 && (
              <div className="bg-amber-50/90 border border-amber-200 rounded-xl p-3.5 text-xs text-amber-900 flex items-center gap-2 mb-4">
                <span className="text-base">📌</span>
                <span>
                  <strong>Mês Atual ({meses.find((m) => m.chave === mesAtivo)?.label}):</strong> Dos {contagemMesAtual.total} exames deste mês civil, <strong>{contagemMesAtual.vencidos} já venceram no passado</strong> (e constam na aba "Exames Já Vencidos") e <strong>{contagemMesAtual.aVencer} {contagemMesAtual.aVencer === 1 ? 'está' : 'estão'} a vencer</strong> até o fim do mês (computado no card de 90 dias).
                </span>
              </div>
            )}

            {/* Lista de Vencimentos do Mês Ativo */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              {vencimentosMesAtivo.length === 0 ? (
                <div className="text-center py-16">
                  <ClockIcon className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">Nenhum exame ocupacional a vencer neste mês.</p>
                  <p className="text-sm text-gray-400 mt-1">Sua empresa está em conformidade para este período.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-xs text-gray-600">
                    <thead className="bg-gray-50/90 text-[11px] uppercase font-bold text-gray-500 border-b border-gray-200">
                      <tr>
                        <th className="py-2.5 px-3 min-w-[140px] max-w-[180px]">
                          Colaborador
                        </th>
                        <th className="py-2.5 px-3 whitespace-nowrap">
                          CPF
                        </th>
                        <th className="py-2.5 px-3 min-w-[150px] max-w-[200px]">
                          Cargo / Setor
                        </th>
                        <th className="py-2.5 px-3 whitespace-nowrap">
                          Exame Disparador (NR-7)
                        </th>
                        <th className="py-2.5 px-3 whitespace-nowrap">
                          Último Exame
                        </th>
                        <th className="py-2.5 px-3 whitespace-nowrap">
                          Validade Legal
                        </th>
                        <th className="py-2.5 px-3 whitespace-nowrap">
                          Vencimento
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {vencimentosMesAtivo.map((v, index) => {
                        const hojeData = new Date();
                        const vencData = new Date(v.data_vencimento);
                        const isVencido = vencData < hojeData;

                        return (
                          <tr key={index} className="hover:bg-gray-50 transition-colors">
                            <td className="py-2.5 px-3">
                              <div className="font-semibold text-gray-900 leading-snug">{v.colaborador_nome}</div>
                              {v.total_exames_colaborador && v.total_exames_colaborador > 1 && (
                                <span className="text-[10px] text-gray-500">
                                  Bateria com {v.total_exames_colaborador} exames
                                </span>
                              )}
                            </td>
                            <td className="py-2.5 px-3 whitespace-nowrap text-gray-600 font-mono">
                              {formatCPF(v.colaborador_cpf)}
                            </td>
                            <td className="py-2.5 px-3">
                              <div className="text-gray-900 font-medium leading-snug">{v.colaborador_cargo || '-'}</div>
                              <div className="text-[11px] text-gray-500">{v.colaborador_setor || '-'}</div>
                              {v.exposto_a_risco !== undefined && (
                                <span
                                  className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold mt-1 ${
                                    v.exposto_a_risco
                                      ? 'bg-amber-50 text-amber-800 border border-amber-200'
                                      : 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                                  }`}
                                  title={v.motivo_periodicidade}
                                >
                                  {v.exposto_a_risco ? '⚠️ Exposto a Risco (PGR)' : '🛡️ Sem Risco Ocupacional'}
                                </span>
                              )}
                            </td>
                            <td className="py-2.5 px-3 whitespace-nowrap">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-blue-50 text-blue-800 border border-blue-200">
                                {v.exame_disparador_nome || 'Exame Periódico'}
                              </span>
                            </td>
                            <td className="py-2.5 px-3 whitespace-nowrap text-gray-600">
                              {new Date(v.data_ultimo_exame).toLocaleDateString('pt-BR')}
                            </td>
                            <td className="py-2.5 px-3 whitespace-nowrap text-gray-700">
                              <div className="font-medium">{v.prazo_meses} meses</div>
                              {v.motivo_periodicidade && (
                                <div className="text-[10px] text-gray-400 max-w-[130px] truncate" title={v.motivo_periodicidade}>
                                  {v.motivo_periodicidade}
                                </div>
                              )}
                            </td>
                            <td className="py-2.5 px-3 whitespace-nowrap">
                              <span
                                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-bold ${
                                  isVencido
                                    ? 'bg-red-100 text-red-800'
                                    : 'bg-emerald-100 text-emerald-800'
                                }`}
                              >
                                {vencData.toLocaleDateString('pt-BR')}
                                {isVencido ? ' (Vencido)' : ''}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        ) : (
          /* Aba: Colaboradores Sem Data */
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-gray-200 mb-6">
              <div>
                <h2 className="text-lg font-bold text-gray-900">
                  Colaboradores sem Data de Exame Cadastrada
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  Estes colaboradores estão ativos mas não possuem histórico de exame ocupacional para cálculo de vencimento.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-2.5">
                {/* Botão Baixar Modelo Excel */}
                <button
                  type="button"
                  onClick={handleDownloadExcelTemplate}
                  disabled={colaboradoresSemData.length === 0}
                  className="w-full sm:w-60 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 bg-white hover:bg-gray-50 transition-colors shadow-2xs disabled:opacity-50 cursor-pointer"
                >
                  <ArrowDownTrayIcon className="h-4 w-4 text-gray-600 flex-shrink-0" />
                  <span>Baixar Modelo Excel (.xlsx)</span>
                </button>

                {/* Botão Importar Excel */}
                <label
                  htmlFor={fileInputId}
                  className={`w-full sm:w-60 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold text-white bg-primary-600 hover:bg-primary-700 transition-colors shadow-2xs cursor-pointer ${
                    isUploadingExcel ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  <ArrowUpTrayIcon className="h-4 w-4 flex-shrink-0" />
                  <span>{isUploadingExcel ? 'Processando Excel...' : 'Atualizar em Lote (.xlsx)'}</span>
                </label>
                <input
                  id={fileInputId}
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={handleUploadExcel}
                  disabled={isUploadingExcel}
                  className="hidden"
                />
              </div>
            </div>

            {loadingSemData ? (
              <div className="flex flex-col items-center justify-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mb-3" />
                <p className="text-sm font-medium text-gray-500">Localizando colaboradores sem data registrada...</p>
              </div>
            ) : colaboradoresSemData.length === 0 ? (
              <div className="text-center py-16">
                <CheckCircleIcon className="h-12 w-12 text-emerald-500 mx-auto mb-3" />
                <p className="text-lg font-semibold text-gray-900">Excelente!</p>
                <p className="text-gray-500 text-sm mt-1">
                  Todos os seus colaboradores possuem histórico de exame ocupacional registrado.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs text-gray-600">
                  <thead className="bg-gray-50/90 text-[11px] uppercase font-bold text-gray-500 border-b border-gray-200">
                    <tr>
                      <th className="py-2.5 px-3 min-w-[140px] max-w-[200px]">
                        Colaborador
                      </th>
                      <th className="py-2.5 px-3 whitespace-nowrap">
                        CPF
                      </th>
                      <th className="py-2.5 px-3 min-w-[150px] max-w-[220px]">
                        Cargo / Setor
                      </th>
                      <th className="py-2.5 px-3 whitespace-nowrap">
                        Data de Admissão
                      </th>
                      <th className="py-2.5 px-3 whitespace-nowrap">
                        Data do Último Exame (Atualizar)
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {colaboradoresSemData.map((c) => {
                      const isSaving = savingInline[c.id];
                      return (
                        <tr key={c.id} className="hover:bg-gray-50 transition-colors">
                          <td className="py-2.5 px-3 font-semibold text-gray-900 max-w-[200px] break-words">
                            {c.nome}
                          </td>
                          <td className="py-2.5 px-3 whitespace-nowrap text-gray-600 font-mono">
                            {formatCPF(c.cpf)}
                          </td>
                          <td className="py-2.5 px-3 max-w-[220px] break-words">
                            <div className="text-gray-900 font-medium leading-snug">{c.cargo || '-'}</div>
                            <div className="text-[11px] text-gray-500">{c.setor || '-'}</div>
                          </td>
                          <td className="py-2.5 px-3 whitespace-nowrap text-gray-600">
                            {c.data_admissao
                              ? new Date(c.data_admissao).toLocaleDateString('pt-BR')
                              : 'Não informada'}
                          </td>
                          <td className="py-2.5 px-3 whitespace-nowrap">
                            <div className="flex items-center gap-2">
                              <input
                                type="date"
                                max={getHojeISO()}
                                value={inlineDates[c.id] || ''}
                                onChange={(e) =>
                                  setInlineDates((prev) => ({ ...prev, [c.id]: e.target.value }))
                                }
                                className="border border-gray-300 rounded-lg px-2.5 py-1.5 text-xs text-gray-800 focus:ring-2 focus:ring-primary-500 focus:outline-none"
                              />
                              <button
                                type="button"
                                onClick={() => handleSaveInlineDate(c.id)}
                                disabled={isSaving || !inlineDates[c.id]}
                                className="px-3 py-1.5 rounded-lg text-xs font-semibold text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 transition-colors shadow-sm"
                              >
                                {isSaving ? 'Salvando...' : 'Salvar'}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
    </PageContainer>
  );
}
